import React from 'react';

import { useMediaQuery, useTheme } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';

import { updateEmployeeMessage } from '../../messages/actions/update-employee-message';

import {
  isEmployeeMessage,
  mapAnnouncementToGenericMessageItem,
  mapEmployeeMessageToGenericMessageItem,
} from '../employee-message-utils';

import { HTMLStripper } from '../../util/html-stripper';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { useThunkDispatch } from '../../util/use-thunk-dispatch';

type InputProps = {
  orgId: number;
  message: EmployeeOrgMessage | AnnouncementSummary;
  activeEmployeeId: number | null;
  activeGroupId: string | null;
  isComposingNewMessage: boolean;
  groupMembershipCounts: { [groupId: string]: number };
  handleMessageClick: ({
    employeeId,
    groupId,
  }: {
    employeeId?: number | null;
    groupId?: string | number | null;
  }) => () => void;
};

export const RecipientListItem = (props: InputProps) => {
  const {
    orgId,
    message,
    activeEmployeeId,
    activeGroupId,
    isComposingNewMessage,
    groupMembershipCounts,
    handleMessageClick,
  } = props;

  const dispatch = useThunkDispatch();
  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const isEmployeeRecipient = isEmployeeMessage(message);
  const genericMessage = isEmployeeRecipient
    ? mapEmployeeMessageToGenericMessageItem(message)
    : mapAnnouncementToGenericMessageItem(message, groupMembershipCounts[message.employeeGroupId]);

  const isSelectedId =
    (activeEmployeeId && activeEmployeeId === genericMessage.employeeId) ||
    (activeGroupId && activeGroupId === genericMessage.groupId);
  const selected = isSelectedId && !isOnMobile && !isComposingNewMessage;
  const cleanMessageBody = HTMLStripper.strip(message.messageBody) || '';

  const isDismissible = isEmployeeRecipient;
  const canMarkRead = isEmployeeRecipient && genericMessage.isUnread;

  const handleDismissMessage =
    ({
      contactId,
      messageId,
      employeeId,
    }: {
      contactId: number;
      messageId: number;
      employeeId: number;
    }) =>
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      dispatch(
        updateEmployeeMessage(orgId, contactId, messageId, employeeId, {
          isUnread: false,
          isDismissed: true,
        }),
      );
    };

  const handleMarkReadClick =
    ({
      contactId,
      messageId,
      employeeId,
    }: {
      contactId: number;
      messageId: number;
      employeeId: number;
    }) =>
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();
      dispatch(
        updateEmployeeMessage(orgId, contactId, messageId, employeeId, {
          isUnread: false,
        }),
      );
    };

  return (
    <div
      role="presentation"
      className={`u-padding-lg u-border-bottom u-cursor-pointer u-hover-highlight ${
        selected ? 'u-selected-option-border-right' : ''
      }`}
      onClick={handleMessageClick({
        employeeId: genericMessage.employeeId,
        groupId: genericMessage.groupId,
      })}
      data-cy={isEmployeeRecipient ? 'employee-to-message' : 'group-to-message'}
    >
      <div className="u-display-flex u-justifyContent-spaceBetween typo-medium">
        <div>{genericMessage.recipientName}</div>
        <div>{genericMessage.sentAt}</div>
      </div>
      <div className="typo-subtitle u-color-black u-spread u-overflowWrap-breakWord">
        <div className="u-margin-top-sm">
          {cleanMessageBody.length > 100
            ? `${cleanMessageBody.slice(0, 100)}...`
            : cleanMessageBody}
        </div>
        {isDismissible ? (
          <IconButton
            className="u-show-on-hover"
            size="small"
            onClick={handleDismissMessage(message)}
            data-cy="dismiss-message"
          >
            <MaterialDesignIcon iconName={ICON_NAMES.CLOSE} size="sm" />
          </IconButton>
        ) : null}
      </div>
      {canMarkRead ? (
        <div
          role="presentation"
          onClick={handleMarkReadClick(message)}
          className="typo-medium u-color-secondary u-display-inlineBlock u-colorHover-secondaryHighlight"
        >
          Mark As Read
        </div>
      ) : null}
    </div>
  );
};
